import React from 'react';
import { Alert, Button, Icon, Intent, ProgressBar, Spinner, Tab, Tabs, Tag } from '@blueprintjs/core';
import PanelWrapper from '../layoutv2/PanelWrapper';
import { getExtensionConfig, DOWNLOAD_ASSETS_URL, EXTENSION_ASSETS_URL, installUserExtension, uninstallUserExtension, fetchExtensionInfo } from '../../services/api'
import Markdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { setInstallingExtension, clearInstallingExtension, unregisterExtension, registerExtension } from './extensions-actions';
import { uninstallInstalledExtension } from './uninstallExtension';
import { useDispatch, useSelector } from 'react-redux';
import { config } from 'process';
import ActivityBarIcon2 from '../../components/ActivityBarIcon2';
import { set } from 'date-fns-jalali';


const ExtensionInfo = ({ params }) => {
    const [info, setInfo] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [extIconUrl, setExtIconUrl] = React.useState(null);
    const [extConfig, setExtConfig] = React.useState(null);
    const [selectedTab, setSelectedTab] = React.useState('details');
    const [details, setDetails] = React.useState(null);
    const [changelog, setChangelog] = React.useState(null);
    const { installingExtensionName, installingExtension, installedExtensions } = useSelector(state => state.extensions);
    const { userDetails } = useSelector(state => state.session);
    const platforms = React.useMemo(() => {
        if (!extConfig?.platform) return [];
        if (Array.isArray(extConfig.platform)) {
            return extConfig.platform.filter(Boolean).map((platform) => platform.toLowerCase());
        }
        return [extConfig.platform].filter(Boolean).map((platform) => platform.toLowerCase());
    }, [extConfig]);

    globalThis['BODAPRO_EXTENSIONS'] = globalThis['BODAPRO_EXTENSIONS'] || [];
    const [installUninstall, setInstallUninstall] = React.useState(null);
    
    //acknowledge extension installation
    const [showAlert, setShowAlert] = React.useState(false);
    const [alertMsg, setAlertMsg] = React.useState(null);
    const [installError, setInstallError] = React.useState(null);

    const dispatch = useDispatch();

    const handleTabChange = (tabId) => setSelectedTab(tabId);

    // Redux is checked first so the buttons re-render after an install/uninstall;
    // the global is the fallback for extensions the loader registered directly.
    const isExtensionInstalled = (name) => {
        return (installedExtensions || []).indexOf(name) > -1
            || globalThis['BODAPRO_EXTENSIONS'].indexOf(name) > -1;
    }

    const addExtension = async () => {
        if(bodapro?.platformType === 'web'){
            if(!info?.id){
                console.log('Missing extension info for web install', { info, extConfig });
                setInstallError('Could not resolve extension details.');
                dispatch(clearInstallingExtension());
                return;
            }

            if(!userDetails?.token){
                setInstallError('Please sign in to install extensions.');
                dispatch(clearInstallingExtension());
                return;
            }

            try{
                const payload = {
                    extension_id: info.id,
                    installed_version: extConfig?.version,
                };

                console.log('Submitting installUserExtension payload', payload);
                await installUserExtension(userDetails.token, payload);

                if(globalThis['BODAPRO_EXTENSIONS']?.indexOf(extConfig.name) === -1){
                    globalThis['BODAPRO_EXTENSIONS'].push(extConfig.name);
                }
                dispatch(registerExtension(extConfig.name));
                setShowAlert(true);
                setAlertMsg('Extension installed successfully. Reload BodaPro to effect changes.');
                setInstallError(null);
            }catch(e){
                console.log('Web extension install failed', e);
                setInstallError('Installation failed');
            }

            dispatch(clearInstallingExtension());
            return;
        }

        const config = await bodapro.getConfig();

        console.log('config:', config);

        dispatch(setInstallingExtension(extConfig.name));
        const result = await bodapro.installExtension({
            extension: extConfig,
            url: `${DOWNLOAD_ASSETS_URL}/${extConfig.name}.zip`,
            bodaProConfigFile: config.configFile
        });

        if(result?.status === 'success'){
            setShowAlert(true);
            setAlertMsg('Extension installed successfully. Restart/reload BodaPro to effect changes');

            //@todo: activate
            // BodaProContext.activateExtension(extConfig.name);
            // window['bodapro-context'].activateExtension(extConfig.name);
        }else{
            //show error message
            setInstallError('Installation failed');
        }

        dispatch(clearInstallingExtension());
    }

    const uninstallExtension = async (extName) => {
        dispatch(setInstallingExtension(extConfig.name));
        if(bodapro?.platformType === 'web'){
            if(!info?.id){
                console.log('Missing extension info for web uninstall', { info, extConfig });
                setInstallError('Could not resolve extension details.');
                dispatch(clearInstallingExtension());
                return;
            }

            if(!userDetails?.token){
                setInstallError('Please sign in to uninstall extensions.');
                dispatch(clearInstallingExtension());
                return;
            }

            try{
                console.log('Submitting uninstallUserExtension call', { extensionId: info.id });
                await uninstallUserExtension(userDetails.token, info.id);
                dispatch(unregisterExtension(extName));
                globalThis['BODAPRO_EXTENSIONS'] = (globalThis['BODAPRO_EXTENSIONS'] || []).filter(v => v !== extName);
                setShowAlert(true);
                setAlertMsg('Extension uninstalled successfully. Restart/reload BodaPro to effect changes');
                setInstallError(null);
            }catch(e){
                console.log('web uninstall error:', e);
                setInstallError('Uninstallation failed');
            }

            dispatch(clearInstallingExtension());
            return;
        }

        const result = await uninstallInstalledExtension(extName, dispatch);

        if(result.success){
            setShowAlert(true);
            setAlertMsg('Extension uninstalled successfully. Restart/reload BodaPro to effect changes');
            setInstallError(null);
        }else{
            console.log('uninstall error:', result.message);
            setInstallError(result.message || 'Uninstallation failed');
        }

        dispatch(clearInstallingExtension());
    }

    React.useEffect(() => {

        const fn = async () => {
            try {
                const [config, extensionInfo] = await Promise.all([
                    getExtensionConfig(params.extension),
                    fetchExtensionInfo(params.extension)
                ]);

                console.log('config:', config);
                setExtConfig(config);
                setExtIconUrl(config?.icon);
                setInfo(extensionInfo);

                //get details 
                const resp = await fetch(`${EXTENSION_ASSETS_URL}/${params.extension}/docs/details.md`);
                const detailsText = await resp.text();
                setDetails(detailsText);
                
                //change log 
                const resp2 = await fetch(`${EXTENSION_ASSETS_URL}/${params.extension}/docs/changelog.md`);
                const changelogText = await resp2.text();
                setChangelog(changelogText);

            } catch (e) {
                console.log('extension info load error', e);
            }

            setLoading(false);
        }

        fn();

    }, [params])

    return <PanelWrapper style={{marginRight: 100}}>

        {loading || extConfig === null ? <Spinner color={Intent.PRIMARY} /> : <>
            <div style={{ display: "flex", flexDirection: "row", marginBottom: 20 }}>
                <div style={{ padding: 5}}>
                    {/* {extIconUrl ? (<img src={`${EXTENSION_ASSETS_URL}/${params.extension}/${extIconUrl}`} width={100} />) : (<Icon icon="cube" size={100} intent="secondary" />)} */}
                    {extIconUrl ? (<ActivityBarIcon2 icon={`${EXTENSION_ASSETS_URL}/${params.extension}/${extIconUrl}`} size={100} />) : (<Icon icon="cube" size={100} intent="secondary" />)}
                </div>
                <div>
                    <div style={{ marginBottom: 5 }}><span style={{ fontSize: 48 }}>{extConfig.label}</span> <span><Tag minimal round>{extConfig.version}</Tag></span></div>
                    <div style={{ marginBottom: 5, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{extConfig.vendor}</span>
                        <Icon icon="download" intent={Intent.PRIMARY} />
                        <span>1,000,566</span>
                    </div>
                    <div style={{ marginBottom: 5 }}>{extConfig.description}</div>
                    {platforms.length > 0 && (
                        <div style={{ marginBottom: 5, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {platforms.map((platform) => (
                                <Tag key={platform} minimal intent={Intent.PRIMARY} round>
                                    {platform}
                                </Tag>
                            ))}
                        </div>
                    )}
                    <div style={{marginBottom: 10}}></div>

                    {installingExtensionName !== extConfig.name && !isExtensionInstalled(extConfig.name) && <Button icon="download" text="Install" small intent={Intent.PRIMARY} onClick={() => {
                        setInstallUninstall('Install');
                       
                    }}
                    disabled={installingExtensionName === extConfig.name}
                    />} &nbsp;

                    {installingExtensionName === extConfig.name && <Button icon="cross" text="Cancel" small intent={Intent.NONE} onClick={() => {
                        dispatch(clearInstallingExtension());
                   
                     }} />}

                    {isExtensionInstalled(extConfig.name) && <Button icon="trash" text="Uninstall" small intent={Intent.DANGER} 
                        onClick={() => {
                            setInstallUninstall('Uninstall');
                        }}
                    />}

                </div>
            </div>

            {installingExtensionName ===  extConfig.name && <ProgressBar intent={Intent.PRIMARY}/>}


            <div>
                <Tabs id="extension-info" onChange={handleTabChange} selectedTabId={selectedTab} large>
                    <Tab id="details" title="DETAILS" panel={(prps) => <Markdown rehypePlugins={[rehypeHighlight, rehypeRaw]} urlTransform={uri =>uri.startsWith("http") ? uri : `${EXTENSION_ASSETS_URL}/${params.extension}/docs/${uri}`}>{details}</Markdown>} />
                    <Tab id="changelog" title="VERSION HISTORY" panel={(prps) => <Markdown rehypePlugins={[rehypeHighlight]}>{changelog}</Markdown>}/>
                </Tabs>
               
            </div>
        </>}


        <Alert
            cancelButtonText="Cancel"
            confirmButtonText="Reload Now"
            isOpen={showAlert}
            onClose={() => { 
                setShowAlert(false);
                setInstallError(null);
                setAlertMsg(null);
            }}
            icon="cube"
            intent={installError ? Intent.DANGER : Intent.PRIMARY }
            onConfirm={() => {
                window.location.reload();
            }}
        >
            <p>{alertMsg}</p>
        </Alert>

        <Alert
            cancelButtonText="Cancel"
            confirmButtonText={installUninstall === "Install" ? "Install" : "Uninstall"}
            isOpen={installUninstall !== null}
            onClose={() => { 
                setInstallUninstall(null);
            }}
            icon="cube"
            intent={installError ? Intent.DANGER : Intent.PRIMARY }
            onConfirm={() => {      

                dispatch(setInstallingExtension(extConfig.name));

                if(installUninstall === 'Uninstall') uninstallExtension(extConfig.name);

                if(installUninstall === 'Install') { 
                    addExtension();
                }

                setInstallUninstall(null);
            }}
        >
            <p>{installUninstall === 'Install' && "Confirm installation of extension."}</p>
            <p>{installUninstall === 'Uninstall' && "Confirm uninstallation of extension."}</p>
        </Alert>
    </PanelWrapper>
}

export default ExtensionInfo;